import { addDays, clubsOnDay, timeOf, todayIso, useClubMemberships, useClubs } from '@shkola/api';
import { Link } from '@tanstack/react-router';
import { type ReactNode, type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRightIcon, CaretRightIcon } from '../../design/icons';
import { useAge } from '../../design/ui';
import { ArtSheet } from './ArtSheet';
import { DoneArt } from './Art';
import { type TaskGroup, useNow, useTaskGroups } from './blocks';
import { TodoLinkTo } from './Cards';
import { FaceAvatar, MoodArt, TaskArt, type TaskArtKind, useFace } from './LineArt';
import {
  type StudentContext,
  capitalize,
  dayLong,
  isoWeekday,
  useShownDay,
  weekdayKey,
} from './model';
import {
  DARK,
  Doodle,
  FLAT,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  Shape,
  grad,
} from './ShapesArt';
import { Head, OpenArrow, ShapesDay, useDismiss } from './ShapesDay';
import { Grades, Moment, MoodBody, School, useMood } from './ShapesHome';
import { type Sim, useSim } from './Situations';

/*
 * «Форми 2» — the look he chose to go on with (28.10: «самый прикольный, с этим можно работать и
 * распространять по системе»). Soft gradients from light to a colour, the words in that colour's ink;
 * the ink and the dark are a near-black violet; pictures in one loose line over a soft shape (his
 * «эталон», the ManyChat illustrations — LineArt), one for each subject, kind of task and of event.
 *  — the greeting is no card: «привіт,» by hand (the one handwritten line), the name big, the avatar
 *    the student picks; the day in a short sentence whose counts are marks — soft rectangles, never
 *    pills; beside it «Зараз» on lime with «Далі» inside;
 *  — a button is a pill with a ringed icon at its end, or a ringed circle; anything else is a label;
 *  — the lessons with the week on top, what to do, the new grades, the school; the blocks of the moment
 *    come and go as before.
 */

const CARD = 'flex min-w-0 flex-col rounded-[28px] bg-white p-5 md:p-6';

/** The top of the sticky lessons: under the bar, or higher for a list taller than the window. */
function useStickyTop(ref: RefObject<HTMLDivElement | null>) {
  const [top, setTop] = useState(80);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const fit = () => setTop(Math.min(80, window.innerHeight - el.offsetHeight - 16));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [ref]);
  return top;
}

// —— the greeting ——

/**
 * The avatar, big, and on it how the student is today (his «"Як ти?" всё равно какой-то отдельный, не
 * смотрится целостно», 28.10): the mood is the avatar's status — its face on the avatar's edge, the face
 * that asks before it is told. One button opens the mood; the face is picked in the settings (the edit
 * pencil stood out — «сильно выбивается»; his «пункт настроек, и там всё менять»).
 */
function Avatar({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const [face] = useFace(ctx.child.memberId);
  const mood = useMood(ctx);
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const initials = `${ctx.child.firstName[0] ?? ''}${ctx.child.lastName[0] ?? ''}`.toUpperCase();
  const status = mood.mood
    ? `${t('studentHome.glass2.mood.title')} ${t(`care.moods.${mood.mood}`)}`
    : t('studentHome.glass2.mood.title');
  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-label={status}
        title={status}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="group relative block rounded-full"
      >
        <span className="block transition-transform duration-300 ease-spring group-hover:scale-[1.03] md:hidden">
          <FaceAvatar face={face} initials={initials} size={84} />
        </span>
        <span className="hidden transition-transform duration-300 ease-spring group-hover:scale-[1.03] md:block">
          <FaceAvatar face={face} initials={initials} size={116} />
        </span>
        {/* the mood on the avatar's edge, cut out of the paper */}
        <span
          className="absolute -right-1 -bottom-1 rounded-full p-[3px] transition-transform duration-300 ease-spring group-hover:scale-110 md:right-0 md:bottom-0"
          style={{ backgroundColor: PAPER }}
        >
          <span className="block md:hidden">
            <MoodArt mood={mood.mood} size={30} />
          </span>
          <span className="hidden md:block">
            <MoodArt mood={mood.mood} size={38} />
          </span>
        </span>
      </button>
      {open && (
        <div
          className="absolute top-full left-0 z-40 mt-3 w-[min(360px,calc(100vw-48px))] rounded-[28px] bg-white p-5"
          style={{ boxShadow: 'inset 0 0 0 1px #E4E0D7, 0 24px 48px -28px rgb(31 26 53 / 0.45)' }}
        >
          <MoodBody state={mood} soft />
          {/* the face itself is picked in the settings (his «пункт настроек, и там всё менять») */}
          <Link
            to="/settings"
            className="group mt-2 flex items-center gap-3 rounded-full py-1.5 pr-3 pl-1.5 text-[13px] font-medium transition-colors hover:bg-[#F5F3EF]"
            style={{ color: INK }}
          >
            <FaceAvatar face={face} initials={initials} size={30} />
            <span className="min-w-0 flex-1 truncate">{t('settings.change')}</span>
            <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
          </Link>
        </div>
      )}
    </div>
  );
}

/** A label set into the words: a soft rectangle in a colour — a mark, not a button. */
function Mark({ tone, children }: { tone: Grad; children: ReactNode }) {
  return (
    <span
      className="rounded-[10px] px-2 py-0.5 whitespace-nowrap"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {children}
    </span>
  );
}

/** The day in one short sentence, its counts marked: «Сьогодні — 7 уроків, 2 контрольні, потім Шахи». */
function DayLine({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const day = useShownDay(ctx);
  const clubs = useClubs(ctx.schoolId);
  const memberships = useClubMemberships([ctx.child.memberId]);
  if (day.pending) return <p className="h-9" aria-busy />;
  const held = day.lessons.filter((l) => l.status !== 'cancelled');
  if (!day.date || held.length === 0)
    return (
      <p className="text-[22px] leading-9 font-light" style={{ color: INK_SOFT }}>
        {t('studentHome.glass.now.noLessons')}
      </p>
    );
  const tests = held.filter((l) => l.isTest).length;
  const club = clubsOnDay(
    clubs.data ?? [],
    memberships.data ?? [],
    ctx.child.memberId,
    isoWeekday(day.date),
  )[0];
  const when = day.isToday
    ? t('studentHome.shapes.today')
    : day.date === addDays(todayIso(), 1)
      ? t('studentHome.shapes.tomorrow')
      : capitalize(t(`student.on.${weekdayKey(day.date)}`));
  return (
    <p
      className="max-w-[620px] font-display text-[21px] leading-[38px] font-light tracking-[-0.01em] md:text-[24px] md:leading-[42px]"
      style={{ color: INK_SOFT }}
    >
      <span style={{ color: INK }}>{when}</span> —{' '}
      <Mark tone="lavender">{t('student.lessonsCount', { count: held.length })}</Mark>
      {tests > 0 && (
        <>
          , <Mark tone="coral">{t('studentHome.shapes.tests', { count: tests })}</Mark>
        </>
      )}
      {club && (
        <>
          , {t('studentHome.shapes.then')} <Mark tone="mint">{club.club.title}</Mark>
        </>
      )}
      .
    </p>
  );
}

/** «Зараз» on lime: what goes on and how long still, then the next lesson on a white row. */
function NowCard({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const state = useNow(ctx);
  if (!state.ready)
    return (
      <div
        className="h-[250px] rounded-[28px]"
        style={{ backgroundImage: grad('lime') }}
        aria-busy
      />
    );
  const { current, next } = state;
  const progress =
    current.progress === undefined ? undefined : Math.min(1, Math.max(0, current.progress));
  return (
    <div
      className="relative flex min-w-0 flex-col rounded-[28px] p-5 md:p-6"
      style={{ backgroundImage: grad('lime'), color: GRAD.lime.ink }}
    >
      <div className="flex items-center gap-2 text-[13px] font-medium">
        <span>{t('studentHome.glass.now.now')}</span>
        {current.aside && (
          <span className="ml-auto flex items-center gap-1.5">
            {current.live && <span className="size-1.5 animate-pulse rounded-full bg-[#FF6A3D]" />}
            {current.aside}
          </span>
        )}
      </div>
      <p
        className="mt-1 truncate font-display text-[36px] leading-[44px] font-light tracking-[-0.035em]"
        style={{ color: INK }}
      >
        {current.title}
      </p>
      {current.sub && <p className="truncate text-[13.5px]">{current.sub}</p>}
      {current.days !== undefined && (
        <p className="text-[13.5px]">
          {t('studentHome.shapes2.daysLeft', { count: current.days })}
        </p>
      )}
      {progress !== undefined && (
        <span className="relative mt-4 block h-[3px] rounded-full bg-white/70">
          <span
            className="absolute inset-y-0 left-0 rounded-full"
            style={{ width: `${progress * 100}%`, backgroundColor: DARK }}
          />
          <span
            className="absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
            style={{ left: `${progress * 100}%`, boxShadow: `inset 0 0 0 3px ${DARK}` }}
          />
        </span>
      )}
      {next ? (
        <div className="mt-5 flex items-center gap-3 rounded-[20px] bg-white p-3 pl-4">
          <span className="flex shrink-0 flex-col leading-tight">
            <span className="text-[11.5px] font-medium" style={{ color: INK_SOFT }}>
              {next.label}
            </span>
            <span
              className="font-display text-[24px] font-light tracking-[-0.02em]"
              style={{ color: INK }}
            >
              {timeOf(next.lesson.startsAt)}
            </span>
          </span>
          <span className="h-9 w-px shrink-0 bg-[#E4E0D7]" />
          <span className="min-w-0 flex-1">
            <span
              className="block truncate text-[16px] leading-[22px] font-medium"
              style={{ color: INK }}
            >
              {next.lesson.subject}
            </span>
            <span
              className="block truncate text-[12.5px] leading-[18px]"
              style={{ color: INK_SOFT }}
            >
              {next.extra ?? next.lesson.room ?? ''}
            </span>
          </span>
          <OpenArrow to="/schedule" label={t('studentHome.glass.week.all')} soft />
        </div>
      ) : (
        <p className="mt-5 rounded-[20px] bg-white p-4 text-[14px]" style={{ color: INK_SOFT }}>
          {state.nothing}
        </p>
      )}
    </div>
  );
}

function Hero({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const age = useAge();
  return (
    <section
      className={`relative grid min-w-0 grid-cols-1 items-center gap-6 pt-2 pb-2 lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)] lg:gap-12 ${className}`}
    >
      <div className="relative flex min-w-0 flex-col gap-5">
        <div className="flex min-w-0 items-center gap-4 md:gap-6">
          <Avatar ctx={ctx} />
          <div className="min-w-0">
            <p
              className="-mb-1 -ml-0.5 inline-block -rotate-3 font-hand text-[32px] leading-9 font-bold md:text-[38px] md:leading-10"
              style={{ color: INK_SOFT }}
            >
              {t('studentHome.shapes2.hi')}
            </p>
            <div className="flex min-w-0">
              <h1
                className={`truncate font-display font-light tracking-[-0.045em] ${age === 'junior' ? 'text-[52px] leading-[58px] md:text-[72px] md:leading-[78px]' : 'text-[46px] leading-[52px] md:text-[64px] md:leading-[70px]'}`}
                style={{ color: INK }}
              >
                {ctx.child.firstName}
              </h1>
            </div>
            {/* the date under a star, as in «Форми» (his «выглядит классно в шапке»; the class — not needed) */}
            <p
              className="mt-1.5 flex items-center gap-2 text-[14px] font-medium"
              style={{ color: INK_SOFT }}
            >
              <Shape kind="star" size={13} color={FLAT.orange} />
              {dayLong(todayIso())}
            </p>
          </div>
        </div>
        <DayLine ctx={ctx} />
        <Doodle className="absolute -right-14 bottom-[30%] hidden w-[150px] rotate-[-8deg] xl:block" />
      </div>
      <NowCard ctx={ctx} />
    </section>
  );
}

// —— what to do: soft cards, the count with its picture, the tasks on white rows ——

const GROUP_TONE: Record<string, Grad> = {
  overdue: 'coral',
  soon: 'cobalt',
  tests: 'lavender',
  rework: 'butter',
  ack: 'pink',
  catchUp: 'mint',
  done: 'lime',
};

function TaskCard({
  group,
  wide = false,
  className = '',
}: {
  group: TaskGroup;
  wide?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const tone = GROUP_TONE[group.key] ?? 'lime';
  // his «не прикольно, когда карточка так раскрывается — не самое логичное для дашборда» (28.10): a few
  // rows only; the rest is one more row into the section, the ↗ kind of button
  const shown = group.rows.slice(0, wide ? 3 : 2);
  const hidden = group.rows.length - shown.length;
  return (
    <div
      className={`relative isolate flex min-w-0 flex-col gap-3 overflow-hidden rounded-[24px] p-4 md:p-5 ${wide ? 'sm:grid sm:grid-cols-[minmax(0,4fr)_minmax(0,7fr)] sm:items-center sm:gap-5' : ''} ${className}`}
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <div className="relative flex min-w-0 flex-col">
        {/* the picture big and quiet behind the words (his «крупней, на фоне, не бросается в глаза, в
            градиенте») — in the corner of the title, never over the rows and their arrows (his «иллюстрации
            не могут налазить на кнопки») */}
        <TaskArt
          kind={group.key as TaskArtKind}
          tone={tone}
          size={112}
          ghost
          className={`pointer-events-none absolute -top-7 -right-5 -z-10 ${wide ? 'sm:top-1/2 sm:-right-3 sm:-translate-y-1/2' : ''}`}
        />
        <p className="min-w-0 truncate text-[15px] leading-5 font-medium">{group.title}</p>
        {group.text ? (
          <p className="mt-2 max-w-[260px] text-[15px] leading-[22px]">{group.text}</p>
        ) : (
          <p className="font-display text-[52px] leading-[56px] font-light tracking-[-0.04em]">
            {group.count}
          </p>
        )}
      </div>
      {shown.length > 0 && (
        <div className="flex min-w-0 flex-col gap-1.5">
          {shown.map((r) => (
            <TodoLinkTo
              key={r.key}
              link={r.link}
              className="group flex min-h-[52px] items-center gap-3 rounded-[16px] bg-white/90 px-3.5 py-2 transition-colors hover:bg-white"
            >
              <span className="min-w-0 flex-1">
                <span
                  className="block truncate text-[14px] leading-5 font-medium"
                  style={{ color: INK }}
                >
                  {r.title}
                </span>
                <span className="block truncate text-[12px] leading-4" style={{ color: INK_SOFT }}>
                  {r.sub}
                </span>
              </span>
              <CaretRightIcon
                size={12}
                weight="bold"
                aria-hidden
                color={INK}
                className="shrink-0 transition-transform group-hover:translate-x-0.5"
              />
            </TodoLinkTo>
          ))}
          {hidden > 0 && (
            <TodoLinkTo
              link={group.link}
              className="group flex h-10 items-center gap-3 rounded-[16px] bg-white/45 px-3.5 text-[13px] font-medium transition-colors hover:bg-white/80"
            >
              <span className="min-w-0 flex-1 truncate" style={{ color: INK }}>
                {t('studentHome.shapes2.moreTasks', { count: hidden })}
              </span>
              <ArrowUpRightIcon
                size={13}
                weight="bold"
                aria-hidden
                color={INK}
                className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </TodoLinkTo>
          )}
        </div>
      )}
    </div>
  );
}

function Todo({ ctx, sim }: { ctx: StudentContext; sim: ReadonlySet<Sim> }) {
  const { t } = useTranslation();
  const tasks = useTaskGroups(ctx);
  // what to confirm stands on top in a band
  let groups = tasks.groups.filter((g) => g.key !== 'ack');
  if (sim.has('notasks')) groups = [];
  if (sim.has('onetask')) groups = groups.slice(0, 1);
  if (!tasks.pending && groups.length === 0)
    groups = [
      {
        key: 'done',
        tone: 'green',
        art: DoneArt,
        title: t('studentHome.glass.board.done'),
        count: 0,
        rows: [],
        link: { to: '/homework' },
        text: t('studentHome.glass.board.doneText'),
      },
    ];
  const total = groups.filter((g) => g.key !== 'done').reduce((n, g) => n + g.count, 0);
  const odd = groups.length % 2 === 1;
  const keys = groups.map((g) => g.key).join(',');
  const line = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (line.current && keys) line.current.scrollLeft = 0;
  }, [keys]);
  return (
    <section className={CARD}>
      <Head title={t('studentHome.glass.todo')} count={total}>
        <OpenArrow to="/homework" label={t('studentHome.glass.all')} soft />
      </Head>
      {tasks.pending && groups.length === 0 ? (
        <div
          className="h-[200px] animate-pulse rounded-[24px]"
          style={{ backgroundColor: PAPER }}
          aria-busy
        />
      ) : (
        <div
          ref={line}
          className={`no-scrollbar grid snap-x snap-mandatory grid-flow-col gap-3 overflow-x-auto sm:grid-flow-row sm:grid-cols-2 sm:overflow-visible ${groups.length === 1 ? 'auto-cols-[100%]' : 'auto-cols-[86%] sm:auto-cols-auto'}`}
        >
          {groups.map((g, i) => (
            <TaskCard
              key={g.key}
              group={g}
              wide={odd && i === 0}
              className={`snap-start ${odd && i === 0 ? 'sm:col-span-2' : ''}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// —— the page ——

export function Shapes2Home({ ctx }: { ctx: StudentContext }) {
  const sim = useSim();
  const day = useRef<HTMLDivElement>(null);
  const top = useStickyTop(day);
  if (sim.has('art')) return <ArtSheet />;
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <Moment ctx={ctx} soft className="xl:col-span-12" />
      <Hero ctx={ctx} className="xl:col-span-12" />
      <div ref={day} className="min-w-0 xl:sticky xl:col-span-5 xl:self-start" style={{ top }}>
        <ShapesDay ctx={ctx} soft />
      </div>
      {/* beside the lessons: a plain stack — a block of the moment comes or goes, the others close up */}
      <div className="flex min-w-0 flex-col gap-4 xl:col-span-7">
        <Todo ctx={ctx} sim={sim} />
        <Grades ctx={ctx} sim={sim} soft />
        <School ctx={ctx} sim={sim} soft />
      </div>
    </div>
  );
}
